/**
 * Interface languages shipped with lemmy-ui.
 * Must match resource filenames in lemmy-translations/frontend/*.json
 * (without .json), except `en` which is also statically bundled.
 *
 * Keep this list in sync with ALLOWED_TRANSLATION_RESOURCES in
 * generate_translations.js — otherwise build will regenerate deleted files,
 * or the UI will offer languages whose chunks were never generated.
 */
export type TranslationDesc = {
  // Name of the translation file in `lemmy-translations`
  resource: string;
  // Name of the language in datefns library (undefined if it is equal to `resource`)
  datefns_resource?: string;
  // Short codename
  code: string;
  // Human readable language name
  name: string;
  bundled?: boolean;
};

export const allLanguages: TranslationDesc[] = [
  {
    resource: "en",
    // There is no general variant for `en` in datefns, only region specific ones
    datefns_resource: "en-US",
    code: "en",
    name: "English",
    bundled: true,
  },
  {
    resource: "zh",
    datefns_resource: "zh-CN",
    code: "zh-CN",
    name: "中文 (简体)",
  },
  { resource: "es", code: "es", name: "Español" },
  { resource: "fr", code: "fr", name: "Français" },
  {
    resource: "pt_BR",
    datefns_resource: "pt-BR",
    code: "pt-BR",
    name: "Português (Brasil)",
  },
  { resource: "de", code: "de", name: "Deutsch" },
  { resource: "ja", code: "ja", name: "日本語" },
  { resource: "ko", code: "ko", name: "한국어" },
];

/**
 * ISO codes used by Lemmy's `language` table for discussion-language pickers.
 * Derived from {@link allLanguages}; always includes `und` (Undetermined).
 */
export function allowedDiscussionLanguageCodes(): Set<string> {
  const codes = new Set<string>(["und"]);
  for (const lang of allLanguages) {
    codes.add(lang.resource);
    codes.add(lang.code);
    codes.add(lang.resource.split(/[-_]/)[0]!);
    codes.add(lang.code.split(/[-_]/)[0]!);
  }
  return codes;
}

/** Keep only DB languages that match the configured UI language allowlist. */
export function filterLanguagesByAllowlist<T extends { code: string }>(
  languages: T[],
): T[] {
  const allowed = allowedDiscussionLanguageCodes();
  return languages.filter(l => allowed.has(l.code));
}
